import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Grid, Paper, Typography, IconButton, Button, Box, Drawer, CircularProgress, useMediaQuery, useTheme } from '@mui/material';
import DataEntryTable from '../components/DataEntryTable';
import ChartSection from '../components/ChartSection';
import NavigationBar from '../components/NavigationBar';
import ExampleDataBanner from '../components/ExampleDataBanner';
import exampleDataset from '../data/exampleDataset';
import { exportDatasetCsv, extractMeasurements } from '../utils/exportCsv';
import PaidAction from '../components/PaidAction';
import SaveAsPartFeatureDialog from '../components/parts/SaveAsPartFeatureDialog';
import { ChevronLeft, Clear as ClearIcon, Send as SendIcon, TableChart as TableChartIcon, Download as DownloadIcon } from '@mui/icons-material';

function CockpitPage() {
    const location = useLocation();
    const [currentDataset, setCurrentDataset] = useState(null);
    const [isCollapsed, setIsCollapsed] = useState(false);
    const [showExampleBanner, setShowExampleBanner] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    // UX-9: concise submit-error signal lifted from DataEntryTable (mirrors
    // `submitting`/onLoadingChange) so ChartSection's stalled-chart message can
    // name the actual failure instead of a generic "not yet available" — the
    // Data Entry drawer holding the full error Alert is closed by default on
    // mobile, so a failed first-visit auto-run would otherwise be invisible.
    const [submitError, setSubmitError] = useState(null);
    // fe-parts lane bridge (design doc §5:416, §2.5:286): the anonymous->part
    // "Save as part feature" action. SaveAsPartFeatureDialog itself was already
    // built and self-contained (frontend/src/components/parts/SaveAsPartFeatureDialog.js)
    // — this is only the trigger wiring, the part that lane's file scope excluded.
    const [saveAsPartOpen, setSaveAsPartOpen] = useState(false);
    const dataEntryRef = useRef(null);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    // On first visit, load example data so the user sees a live analysis immediately.
    // `autoSubmit: true` tells DataEntryTable to fire Submit itself, deterministically,
    // once it has actually seeded its state from this dataset (see DataEntryTable's
    // `pendingAutoSubmit` state flag + ready-effect) — not a guessed wall-clock delay.
    // See UX-8: docs/investigations/ux8-first-visit-autorun-chart.md.
    useEffect(() => {
        const hasVisited = localStorage.getItem('cc_hasVisited');
        if (!hasVisited) {
            setCurrentDataset({ ...exampleDataset, autoSubmit: true });
            setShowExampleBanner(true);
        }
    }, []);

    // Mobile: start with the data-entry panel collapsed so charts are full-width.
    useEffect(() => { setIsCollapsed(isMobile); }, [isMobile]);

    // Load dataset from navigation state (from History page)
    useEffect(() => {
        if (location.state?.dataset) {
            loadDataset(location.state.dataset);
            // Clear the state to prevent reloading on refresh
            window.history.replaceState({}, document.title);
        }
    }, [location.state]);

    const loadDataset = (dataset) => {
        if (!dataset || !dataset.values) {
            console.error("No values found for this dataset.");
            return;
        }

        try {
            // Check if this is subgroup data
            const isSubgroupData = dataset.subgroups && Object.keys(dataset.subgroups).length > 0;

            let valuesForTable;
            if (isSubgroupData) {
                // Convert subgroups object back to [subgroupId, value] format
                valuesForTable = [];
                Object.keys(dataset.subgroups).forEach(subgroupId => {
                    const subgroupValues = dataset.subgroups[subgroupId];
                    subgroupValues.forEach(value => {
                        valuesForTable.push([subgroupId, Number(value)]);
                    });
                });
            } else {
                // Individual measurements: convert to [null, value] format
                valuesForTable = dataset.values.map(value => [null, Number(value)]);
            }

            setCurrentDataset({
                ...dataset,
                ad_value: parseFloat(dataset.ad_value),
                p_value: parseFloat(dataset.p_value),
                values: valuesForTable,
            });
        } catch (err) {
            console.error('Error loading dataset:', err);
        }
    };

    const handleNewDataset = (newDataset) => {
        if (!newDataset) {
            console.error('No dataset provided');
            return;
        }
        const parsedDataset = {
            ...newDataset,
            ad_value: parseFloat(newDataset.ad_value),
            p_value: parseFloat(newDataset.p_value)
        };
        setCurrentDataset(parsedDataset);
        setShowExampleBanner(false);
        localStorage.setItem('cc_hasVisited', '1');
    };

    const handleClear = () => {
        setCurrentDataset(null);
        setShowExampleBanner(false);
        localStorage.setItem('cc_hasVisited', '1');
    };

    const handleDismissExample = () => {
        setCurrentDataset(null);
        setShowExampleBanner(false);
        localStorage.setItem('cc_hasVisited', '1');
    };

    const handleSubmit = () => {
        if (dataEntryRef.current) {
            dataEntryRef.current.submit();
        }
    };

    const drawerWidth = 320;

    // AUTH-7: premium ACTION gate. The interim passcode button (PremiumButton)
    // was retired at the Phase C cutover — PaidAction (real Clerk tier check) is
    // the only path now.
    const PremiumGate = PaidAction;

    // UX-8: distinguishes "raw input echoed back" (e.g. the first-visit example,
    // before its auto-submit POST resolves) from "a real analysis result", so
    // ChartSection can render a loading state instead of a chart built from
    // mean/std_dev hard-defaulted to 0 (see the `|| 0` fallbacks below). `mean` is
    // always present — and a genuine JS number — on a computed /api/capability
    // response, but absent on the raw example dataset. A history-reloaded dataset
    // carries `mean` as a pg NUMERIC string, so coerce rather than typeof-check it.
    // See docs/investigations/ux8-first-visit-autorun-chart.md.
    const rawMean = currentDataset?.mean;
    const hasComputedStats = rawMean !== undefined && rawMean !== null && Number.isFinite(Number(rawMean));

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden', backgroundImage: `url('/assets/background.webp')`, backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat' }}>
            <NavigationBar />
        <Box
            sx={{
                flex: 1,
                overflow: 'hidden',
                display: 'flex',
            }}
        >
            {/* Data Entry Drawer */}
            <Drawer
                variant={isMobile ? 'temporary' : 'persistent'}
                anchor="left"
                open={!isCollapsed}
                onClose={() => setIsCollapsed(true)}
                ModalProps={{ keepMounted: true }}
                sx={{
                    width: isMobile ? 0 : (isCollapsed ? 0 : drawerWidth),
                    flexShrink: 0,
                    '& .MuiDrawer-paper': {
                        width: drawerWidth,
                        boxSizing: 'border-box',
                        backgroundColor: 'rgba(30, 36, 44, 0.98)',
                        borderRight: '1px solid rgba(144, 202, 249, 0.2)',
                        position: 'relative',
                    },
                }}
            >
                <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', p: 1.5 }}>
                    {/* Drawer header */}
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        mb: 1,
                        pb: 1,
                        borderBottom: '1px solid rgba(144, 202, 249, 0.15)',
                        flexShrink: 0
                    }}>
                        <Typography variant="subtitle2" sx={{ color: 'rgba(255, 255, 255, 0.9)', fontWeight: 500, mr: 'auto' }}>
                            Data Entry
                        </Typography>
                        <IconButton
                            size="small"
                            onClick={handleClear}
                            sx={{ color: 'rgba(255, 255, 255, 0.6)', p: 0.5, '&:hover': { color: '#ff8a80' } }}
                            aria-label="Clear form" title="Clear form"
                        >
                            <ClearIcon fontSize="small" />
                        </IconButton>
                        <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            onClick={handleSubmit}
                            disabled={submitting}
                            startIcon={submitting ? <CircularProgress size={12} color="inherit" /> : <SendIcon />}
                            sx={{ py: 0.25, px: 1.5, fontSize: '0.75rem' }}
                        >
                            {submitting ? 'Submitting…' : 'Submit'}
                        </Button>
                        <IconButton
                            onClick={() => setIsCollapsed(true)}
                            size="small"
                            sx={{ color: 'rgba(144, 202, 249, 0.7)', p: 0.5, ml: 0.5 }}
                            aria-label="Close panel" title="Close panel"
                        >
                            <ChevronLeft fontSize="small" />
                        </IconButton>
                    </Box>
                    {showExampleBanner && (
                        <ExampleDataBanner onDismiss={handleDismissExample} />
                    )}
                    <DataEntryTable
                        ref={dataEntryRef}
                        onNewDataset={handleNewDataset}
                        dataset={currentDataset}
                        onLoadingChange={setSubmitting}
                        onSubmitError={setSubmitError}
                    />
                </Box>
            </Drawer>

            {/* Main Content Area */}
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    p: { xs: 1, sm: 1.5, md: 2 },
                    transition: 'margin 0.3s ease',
                    marginLeft: isCollapsed ? 0 : 0,
                    width: '100%',
                    height: '100vh',
                    overflow: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                {/* Floating button to open drawer when collapsed */}
                {isCollapsed && (
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => setIsCollapsed(false)}
                        startIcon={<TableChartIcon />}
                        sx={{
                            position: 'fixed',
                            left: 16,
                            top: 16,
                            zIndex: 1100,
                            boxShadow: 3,
                        }}
                    >
                        Data Entry
                    </Button>
                )}

                <Grid container spacing={0} sx={{ flex: 1, width: '100%', minHeight: 0 }}>
                    {/* Charts Section */}
                    <Grid item xs={12}>
                    <Paper id="printable-analysis" sx={{
                        padding: 2,
                        width: '100%',
                        backgroundColor: 'rgba(40, 44, 52, 0.92)',
                    }}>
                        {/* Analysis toolbar — hidden in print. Export CSV is free (#6);
                            Print / PDF is a premium feature (#5). */}
                        {currentDataset && extractMeasurements(currentDataset).length > 0 && (
                            <Box className="no-print" sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 1 }}>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<DownloadIcon />}
                                    onClick={() => exportDatasetCsv(currentDataset)}
                                    aria-label="Export current analysis as CSV"
                                    sx={{ color: '#90caf9', borderColor: 'rgba(144,202,249,0.5)', '&:hover': { borderColor: '#90caf9', backgroundColor: 'rgba(144,202,249,0.08)' } }}
                                >
                                    Export CSV
                                </Button>
                                <PremiumGate
                                    onAction={() => window.print()}
                                    feature="the print / PDF report"
                                    size="small"
                                    variant="outlined"
                                    aria-label="Print or save the analysis as PDF"
                                    sx={{ color: '#ffd54f', borderColor: 'rgba(255,213,79,0.4)', '&:hover': { borderColor: '#ffd54f', backgroundColor: 'rgba(255,213,79,0.08)' } }}
                                >
                                    Print / PDF
                                </PremiumGate>
                                <PremiumGate
                                    onAction={() => setSaveAsPartOpen(true)}
                                    feature="saving this analysis as a part feature"
                                    size="small"
                                    variant="outlined"
                                    aria-label="Save this analysis as a part feature"
                                    sx={{ color: '#ce93d8', borderColor: 'rgba(206,147,216,0.4)', '&:hover': { borderColor: '#ce93d8', backgroundColor: 'rgba(206,147,216,0.08)' } }}
                                >
                                    Save as part feature
                                </PremiumGate>
                            </Box>
                        )}
                        <ChartSection
                            title={currentDataset ? currentDataset.description : null}
                            dataset={currentDataset || { values: [] }}
                            hasComputedStats={hasComputedStats}
                            submitting={submitting}
                            submitError={submitError}
                            usl={currentDataset?.usl ?? null}
                            lsl={currentDataset?.lsl ?? null}
                            mean={currentDataset?.mean || 0}
                            std_dev={currentDataset?.std_dev || 0}
                            within_std_dev={currentDataset?.within_std_dev || (currentDataset?.mrMean / 1.128) || 0}
                            cp={currentDataset?.cp || 0}
                            cpk={currentDataset?.cpk || 0}
                            cpu={currentDataset?.cpu ?? null}
                            cpl={currentDataset?.cpl ?? null}
                            pp={currentDataset?.pp || 0}
                            ppk={currentDataset?.ppk || 0}
                            ppu={currentDataset?.ppu ?? null}
                            ppl={currentDataset?.ppl ?? null}
                            ppm_overall={currentDataset?.ppm_overall || currentDataset?.estimated_ppm || 0}
                            ppm_within={currentDataset?.ppm_within || 0}
                            movingRanges={currentDataset?.movingRanges || []}
                            mrMean={currentDataset?.mrMean || 0}
                            i_ucl={currentDataset?.i_ucl || 0}
                            i_lcl={currentDataset?.i_lcl || 0}
                            mr_ucl={currentDataset?.mr_ucl || 0}
                            mr_lcl={currentDataset?.mr_lcl || 0}
                            orderedQuantiles={currentDataset?.orderedQuantiles || []}
                            theoreticalQuantiles={currentDataset?.theoreticalQuantiles || []}
                            ad_value={currentDataset?.ad_value || 0}
                            p_value={currentDataset?.p_value || 0}
                            referenceLine={currentDataset?.referenceLine || []}
                            lowerBand={currentDataset?.lowerBand || []}
                            upperBand={currentDataset?.upperBand || []}
                            chartType={currentDataset?.chartType || 'i-mr'}
                            subgroupSize={currentDataset?.subgroupSize || 1}
                            xbarValues={currentDataset?.xbarValues || []}
                            rValues={currentDataset?.rValues || []}
                            xbarBar={currentDataset?.xbarBar || 0}
                            rBar={currentDataset?.rBar || 0}
                            xbar_ucl={currentDataset?.xbar_ucl || 0}
                            xbar_lcl={currentDataset?.xbar_lcl || 0}
                            r_ucl={currentDataset?.r_ucl || 0}
                            r_lcl={currentDataset?.r_lcl || 0}
                            subgroupIds={currentDataset?.subgroupIds || []}
                            allValues={currentDataset?.allValues || []}
                            unstable={currentDataset?.unstable}
                            unstableSignalCount={currentDataset?.unstableSignalCount}
                        />
                    </Paper>
                </Grid>

                {/* Disclaimer Section */}
                <Grid item xs={12}>
                    <Paper sx={{ padding: 3, mt: 2, backgroundColor: 'rgba(30, 30, 30, 0.85)', borderTop: '2px solid rgba(255, 255, 255, 0.1)' }}>
                        <Typography variant="h6" sx={{ mb: 2, color: '#90caf9' }}>
                            Disclaimer & Privacy
                        </Typography>

                        <Typography variant="body2" sx={{ mb: 2, lineHeight: 1.6 }}>
                            <strong>Statistical Analysis:</strong> Capability Cockpit provides statistical calculations and visualizations intended for informational and decision-support purposes only.
                            Results are dependent on the accuracy, completeness, and appropriateness of the input data and underlying assumptions.
                            Users are responsible for interpreting outputs and determining their suitability for any specific application.
                            No warranties, express or implied, are made regarding fitness for a particular purpose.
                            Results should not be used as the sole basis for regulatory, quality, or manufacturing decisions without appropriate professional judgment and validation.
                        </Typography>

                        <Typography variant="body2" sx={{ mb: 2, lineHeight: 1.6 }}>
                            <strong>Data Storage & Privacy:</strong> To allow you to view your own prior submissions, this site stores a functional, anonymous identifier in your browser.
                            No personal identifying information is collected. Clearing your browser data may remove access to previous submissions.
                            Shared links, if enabled, allow anyone with the link to view the associated results. You are responsible for managing and revoking shared access.
                            Use of this site constitutes acceptance of these terms.
                        </Typography>

                    </Paper>
                </Grid>
            </Grid>
            <SaveAsPartFeatureDialog
                open={saveAsPartOpen}
                onClose={() => setSaveAsPartOpen(false)}
                values={extractMeasurements(currentDataset || {})}
                usl={currentDataset?.usl ?? null}
                lsl={currentDataset?.lsl ?? null}
                description={currentDataset?.description || ''}
            />
            </Box>
        </Box>
        </Box>
    );
}

export default CockpitPage;
